import React, { useRef, useState, useEffect } from "react";

/**
 * Newton's Cannon — interactive orbital-mechanics demo.
 *
 * Fire a cannonball off a mountaintop and adjust the launch speed to see it
 * crash back, fall into a circular orbit, stretch into an ellipse, or escape.
 * The physics is real: gravity accelerates the projectile toward Earth's
 * center, so "falling around the Earth" emerges on its own.
 *
 * Self-contained. Only dependency is React (hooks). Default export.
 * Next.js App Router: add  'use client'  as the first line of the file.
 */

const COL = {
  bg: "#0c1322", panel: "#162138", panel2: "#1f2d4a", line: "#2a3550",
  ink: "#f2efe4", dim: "#aeb6c7", gold: "#d4af37", goldSoft: "#e6cd77",
  blue: "#3f86a8", terra: "#c56a40", cream: "#f2efe4",
};

// world constants (scaled, self-consistent — not SI, tuned for the screen)
const R = 70;                       // planet radius (world units)
const MTN = 22;                     // mountaintop height
const launchR = R + MTN;            // radius from center where the cannon sits
const GM = 3.2e4;                   // gravitational parameter
const vCirc = Math.sqrt(GM / launchR);
const vEsc = Math.sqrt(2 * GM / launchR);
const KMS_AT_CIRC = 1.6;            // slider km/s that equals circular-orbit speed
const worldPerKms = vCirc / KMS_AT_CIRC;

const PRESETS = [
  { label: "Too slow", detail: "curves down, crashes", kms: 1.05, col: COL.terra },
  { label: "Just right", detail: "circular orbit — the Moon", kms: 1.6, col: COL.gold },
  { label: "Faster", detail: "stretched elliptical orbit", kms: 1.75, col: COL.gold },
  { label: "Escape!", detail: "leaves Earth forever", kms: Math.min(12, vEsc / worldPerKms + 0.2), col: COL.blue },
];

function classify(v) {
  if (v < vCirc * 0.999) return "sub";
  if (v < vEsc * 0.999) return "orbit";
  return "escape";
}

export default function NewtonsCannon() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const sim = useRef({ projectiles: [], active: null, raf: null, last: 0, stars: [], view: { cx: 0, cy: 0, scale: 1 } });
  const speedRef = useRef(1.6);

  const [speed, setSpeed] = useState(1.6);
  const [verdict, setVerdict] = useState({ text: "Ready to fire", color: COL.dim });
  const [readout, setReadout] = useState(
    `orbit speed ≈ ${KMS_AT_CIRC.toFixed(2)} km/s · escape ≈ ${(vEsc / worldPerKms).toFixed(2)} km/s`
  );
  const [explain, setExplain] = useState(
    "Set a speed, then Fire. Too slow and the ball curves down and hits the ground. At just the right speed the curve matches Earth’s — it falls around the planet forever. Faster still and it flies off into space."
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const S = sim.current;

    function W2S(x, y) { return [S.view.cx + x * S.view.scale, S.view.cy - y * S.view.scale]; }

    function makeStars(w, h) {
      S.stars = [];
      const n = Math.round((w * h) / 9000);
      for (let i = 0; i < n; i++) S.stars.push([Math.random() * w, Math.random() * h, Math.random() * 1.3 + 0.2, Math.random() * 0.6 + 0.2]);
    }

    function layout(w, h) {
      S.view.scale = Math.min(w, h) / (R * 3.4);
      S.view.cx = w * 0.5;
      S.view.cy = h * 0.6;
    }

    function resize() {
      const rect = wrapRef.current.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(10, rect.width * dpr);
      canvas.height = Math.max(10, rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      layout(rect.width, rect.height);
      makeStars(rect.width, rect.height);
      draw();
    }

    function updateVerdict(p) {
      const kms = p.kms.toFixed(2);
      let color, label, msg;
      if (p.kind === "sub") {
        color = COL.terra; label = "Crashes back to Earth";
        msg = `At ${kms} km/s the ball curves downward faster than Earth curves away, so it falls back and hits the ground. Newton’s point: this is the same fall as a dropped apple — just with sideways speed.`;
      } else if (p.kind === "orbit") {
        color = COL.gold;
        const circlish = Math.abs(p.kms - KMS_AT_CIRC) < 0.12;
        label = circlish ? "Circular orbit — it falls around forever!" : "Elliptical orbit — it loops around";
        msg = circlish
          ? `At about ${kms} km/s the curve of the fall exactly matches Earth’s curvature. The ball keeps falling around the planet and never lands — it has become a satellite, just like the Moon.`
          : `At ${kms} km/s the ball is too fast to crash but too slow to escape, so it traces a stretched elliptical orbit — swinging far out, then back in.`;
      } else {
        color = COL.blue; label = "Escapes into space";
        msg = `At ${kms} km/s the ball is moving faster than escape speed. Gravity bends its path but can’t pull it back — it leaves Earth behind for good.`;
      }
      setVerdict({ text: label, color });
      setReadout(`speed ${kms} km/s · orbit ≈ ${KMS_AT_CIRC.toFixed(2)} · escape ≈ ${(vEsc / worldPerKms).toFixed(2)}`);
      setExplain(msg);
    }

    function step(p, dt) {
      const sub = 6, h = dt / sub;
      for (let i = 0; i < sub; i++) {
        const r2 = p.x * p.x + p.y * p.y, r = Math.sqrt(r2), a = -GM / r2;
        p.vx += (a * p.x / r) * h; p.vy += (a * p.y / r) * h;
        p.x += p.vx * h; p.y += p.vy * h;
        const nr = Math.sqrt(p.x * p.x + p.y * p.y);
        if (nr <= R) { p.x *= R / nr; p.y *= R / nr; p.alive = false; break; }
        if (nr > R * 14) { p.alive = false; p.escaped = true; break; }
      }
      p.trail.push([p.x, p.y]);
      if (p.trail.length > 2600) p.trail.shift();
    }

    function trailColor(kind) { return kind === "sub" ? COL.terra : kind === "orbit" ? COL.gold : COL.blue; }

    function drawStars() {
      ctx.fillStyle = "#fff";
      for (const s of S.stars) { ctx.globalAlpha = s[3]; ctx.beginPath(); ctx.arc(s[0], s[1], s[2], 0, 7); ctx.fill(); }
      ctx.globalAlpha = 1;
    }
    function drawBlobs(ex, ey, er) {
      ctx.save(); ctx.beginPath(); ctx.arc(ex, ey, er, 0, 7); ctx.clip();
      ctx.fillStyle = "rgba(90,150,110,0.55)";
      const blobs = [[-0.3, -0.2, 0.45], [0.25, 0.05, 0.4], [-0.1, 0.45, 0.35], [0.45, -0.35, 0.28]];
      for (const b of blobs) { ctx.beginPath(); ctx.ellipse(ex + b[0] * er, ey + b[1] * er, b[2] * er, b[2] * er * 0.7, b[0], 0, 7); ctx.fill(); }
      ctx.restore();
    }

    function draw() {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      drawStars();
      const [ex, ey] = W2S(0, 0), er = R * S.view.scale;
      const atmo = ctx.createRadialGradient(ex, ey, er * 0.9, ex, ey, er * 1.5);
      atmo.addColorStop(0, "rgba(63,134,168,0.18)"); atmo.addColorStop(1, "rgba(63,134,168,0)");
      ctx.fillStyle = atmo; ctx.beginPath(); ctx.arc(ex, ey, er * 1.5, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.arc(ex, ey, er, 0, Math.PI * 2);
      const eg = ctx.createRadialGradient(ex - er * 0.3, ey - er * 0.3, er * 0.2, ex, ey, er);
      eg.addColorStop(0, "#4a8ba8"); eg.addColorStop(1, "#1d4a60");
      ctx.fillStyle = eg; ctx.fill();
      ctx.lineWidth = 1.5; ctx.strokeStyle = "rgba(180,210,230,0.3)"; ctx.stroke();
      drawBlobs(ex, ey, er);

      const [mx, my] = W2S(0, launchR), [, groundY] = W2S(0, R);
      ctx.fillStyle = "#6b5a3a";
      ctx.beginPath();
      ctx.moveTo(mx - 10 * S.view.scale, groundY); ctx.lineTo(mx, my); ctx.lineTo(mx + 10 * S.view.scale, groundY);
      ctx.closePath(); ctx.fill();
      ctx.save(); ctx.translate(mx, my);
      ctx.fillStyle = COL.cream; ctx.fillRect(-2, -3.5, 16, 7);
      ctx.fillStyle = COL.gold; ctx.beginPath(); ctx.arc(-2, 0, 5, 0, 7); ctx.fill();
      ctx.restore();

      for (const p of S.projectiles) {
        if (p.trail.length < 2) continue;
        ctx.lineWidth = p === S.active ? 2.4 : 1.6;
        ctx.strokeStyle = trailColor(p.kind);
        ctx.globalAlpha = p === S.active ? 1 : 0.5;
        ctx.beginPath();
        for (let i = 0; i < p.trail.length; i++) {
          const [sx, sy] = W2S(p.trail[i][0], p.trail[i][1]);
          i ? ctx.lineTo(sx, sy) : ctx.moveTo(sx, sy);
        }
        ctx.stroke(); ctx.globalAlpha = 1;
        if (p === S.active && p.alive) {
          const [sx, sy] = W2S(p.x, p.y);
          ctx.fillStyle = COL.cream; ctx.beginPath(); ctx.arc(sx, sy, 4, 0, 7); ctx.fill();
          ctx.strokeStyle = trailColor(p.kind); ctx.lineWidth = 2; ctx.stroke();
        }
        if (!p.alive && !p.escaped) {
          const lastPt = p.trail[p.trail.length - 1], [sx, sy] = W2S(lastPt[0], lastPt[1]);
          ctx.fillStyle = COL.terra; ctx.beginPath(); ctx.arc(sx, sy, 3.5, 0, 7); ctx.fill();
        }
      }
    }

    function tick(now) {
      const dt = Math.min(0.05, (now - S.last) / 1000);
      S.last = now;
      if (S.active && S.active.alive) { step(S.active, dt * 2.2); if (!S.active.alive) S.active = null; }
      draw();
      if (S.active && S.active.alive) S.raf = requestAnimationFrame(tick);
      else S.raf = null;
    }

    function fire() {
      const kms = speedRef.current;
      const v = kms * worldPerKms;
      const p = { x: 0, y: launchR, vx: v, vy: 0, trail: [], kms, alive: true, kind: classify(v) };
      S.active = p;
      S.projectiles.push(p);
      if (S.projectiles.length > 6) S.projectiles.shift();
      updateVerdict(p);
      if (!S.raf) { S.last = performance.now(); S.raf = requestAnimationFrame(tick); }
    }

    function clear() {
      S.projectiles = []; S.active = null;
      if (S.raf) { cancelAnimationFrame(S.raf); S.raf = null; }
      draw();
      setVerdict({ text: "Ready to fire", color: COL.dim });
    }

    // expose controls to the React buttons
    sim.current.fire = fire;
    sim.current.clear = clear;

    // Defer resize into rAF so drawing happens outside the observer's delivery
    // cycle — avoids the benign "ResizeObserver loop" warning some dev overlays
    // (e.g. Create React App) surface as an error.
    let roRaf = null;
    const ro = new ResizeObserver(() => {
      if (roRaf) return;
      roRaf = requestAnimationFrame(() => { roRaf = null; resize(); });
    });
    ro.observe(wrapRef.current);
    resize();

    return () => {
      ro.disconnect();
      if (roRaf) cancelAnimationFrame(roRaf);
      if (S.raf) cancelAnimationFrame(S.raf);
    };
  }, []);

  function onSlider(e) {
    const v = parseFloat(e.target.value);
    speedRef.current = v;
    setSpeed(v);
  }
  function fire() { sim.current.fire && sim.current.fire(); }
  function clear() { sim.current.clear && sim.current.clear(); }
  function usePreset(kms) { speedRef.current = kms; setSpeed(kms); sim.current.fire && sim.current.fire(); }

  const S = styles;
  return (
    <div style={S.root}>
      <style>{`
        .nc-range{-webkit-appearance:none;appearance:none;height:6px;border-radius:4px;width:100%;
          background:linear-gradient(90deg,${COL.blue},${COL.gold},${COL.terra});outline:none}
        .nc-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:22px;height:22px;border-radius:50%;
          background:${COL.ink};border:3px solid ${COL.gold};cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,.5)}
        .nc-range::-moz-range-thumb{width:22px;height:22px;border-radius:50%;background:${COL.ink};
          border:3px solid ${COL.gold};cursor:pointer}
        .nc-range:focus-visible{box-shadow:0 0 0 3px rgba(212,175,55,.4)}
        .nc-btn:focus-visible,.nc-preset:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(212,175,55,.4)}
        .nc-btn:active{transform:translateY(1px)}
      `}</style>

      <header style={S.header}>
        <div style={S.eyebrow}>Lesson 18 · The Falling Moon</div>
        <div style={S.h1}>Newton’s Cannon <span style={S.h1sub}>— fire fast enough and you’ll never come down</span></div>
      </header>

      <div style={S.stage}>
        <div ref={wrapRef} style={S.canvasCard}>
          <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
          <div style={S.verdict}>
            <div style={S.tag}><span style={{ ...S.dot, background: verdict.color }} />{verdict.text}</div>
            <div style={S.readoutBox}>{readout}</div>
          </div>
        </div>

        <div style={S.panel}>
          <div>
            <label htmlFor="nc-spd" style={S.sliderLabel}>
              <span>Cannon speed</span>
              <span style={S.speedVal}>{speed.toFixed(2)} <small style={{ color: COL.dim, fontSize: 12 }}>km/s</small></span>
            </label>
            <input id="nc-spd" className="nc-range" type="range" min="0.6" max="12" step="0.05" value={speed} onChange={onSlider} />
            <div style={S.ticks}><span>slow</span><span>orbit</span><span>escape</span></div>
          </div>

          <div style={S.btnRow}>
            <button className="nc-btn" style={{ ...S.btn, ...S.btnPrimary }} onClick={fire}>🔥 Fire</button>
            <button className="nc-btn" style={S.btn} onClick={clear}>Clear trails</button>
          </div>

          <div style={S.presets}>
            {PRESETS.map((ps) => (
              <button key={ps.label} className="nc-preset" style={S.preset} onClick={() => usePreset(ps.kms)}>
                <span style={{ ...S.sw, background: ps.col }} />
                <span style={{ minWidth: 0 }}>
                  <span style={S.presetLabel}>{ps.label}</span>
                  <span style={S.presetDetail}>{ps.detail}</span>
                </span>
              </button>
            ))}
          </div>

          <div style={S.legend}>
            <span style={S.legendItem}><i style={{ ...S.legI, background: COL.terra }} />crashes back</span>
            <span style={S.legendItem}><i style={{ ...S.legI, background: COL.gold }} />orbits</span>
            <span style={S.legendItem}><i style={{ ...S.legI, background: COL.blue }} />escapes</span>
          </div>

          <div style={S.explain}>{explain}</div>
        </div>
      </div>

      <footer style={S.footer}>
        A thought experiment from Newton’s <i>Principia</i> (1687). Earth size and gravity are scaled for the screen — the physics of “falling around” is real.
      </footer>
    </div>
  );
}

const styles = {
  root: { background: COL.bg, color: COL.ink, borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 12, fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif", maxWidth: 1180, margin: "0 auto", boxSizing: "border-box" },
  header: {},
  eyebrow: { fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: COL.gold, fontWeight: 700, marginBottom: 2 },
  h1: { fontFamily: "Georgia,serif", fontWeight: 700, fontSize: "clamp(20px,3vw,28px)", color: COL.ink },
  h1sub: { color: COL.goldSoft, fontStyle: "italic", fontWeight: 400 },
  stage: { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(260px,320px)", gap: 14 },
  canvasCard: { position: "relative", background: "#070b16", border: `1px solid ${COL.line}`, borderRadius: 12, overflow: "hidden", minHeight: 360, minWidth: 0 },
  verdict: { position: "absolute", left: 14, top: 14, right: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", pointerEvents: "none" },
  tag: { fontSize: 13, fontWeight: 700, padding: "6px 12px", borderRadius: 999, background: COL.panel, border: `1px solid ${COL.line}`, color: COL.ink, boxShadow: "0 2px 10px rgba(0,0,0,.35)" },
  dot: { display: "inline-block", width: 9, height: 9, borderRadius: "50%", marginRight: 7, verticalAlign: "middle" },
  readoutBox: { fontFamily: "ui-monospace,Menlo,Consolas,monospace", fontSize: 12, color: COL.dim, background: "rgba(10,14,24,.7)", border: `1px solid ${COL.line}`, borderRadius: 8, padding: "5px 10px", fontVariantNumeric: "tabular-nums" },
  panel: { background: COL.panel, border: `1px solid ${COL.line}`, borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 16, minWidth: 0 },
  sliderLabel: { display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", color: COL.gold, fontWeight: 700, marginBottom: 8 },
  speedVal: { fontFamily: "Georgia,serif", fontSize: 20, color: COL.ink, letterSpacing: 0, textTransform: "none", fontVariantNumeric: "tabular-nums" },
  ticks: { display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: COL.dim },
  btnRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  btn: { fontSize: 13, fontWeight: 600, padding: "9px 14px", borderRadius: 8, cursor: "pointer", border: `1px solid ${COL.line}`, background: COL.panel2, color: COL.ink, fontFamily: "inherit" },
  btnPrimary: { background: COL.gold, color: "#1a1300", borderColor: COL.gold },
  presets: { display: "flex", flexDirection: "column", gap: 7 },
  preset: { display: "flex", alignItems: "center", gap: 10, textAlign: "left", width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COL.line}`, background: COL.panel2, color: COL.ink, cursor: "pointer", fontFamily: "inherit" },
  sw: { width: 10, height: 10, borderRadius: "50%", flex: "none" },
  presetLabel: { fontWeight: 700, fontSize: 13, display: "block" },
  presetDetail: { fontSize: 11, color: COL.dim, display: "block", marginTop: 1 },
  legend: { display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, color: COL.dim },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 5 },
  legI: { width: 14, height: 3, borderRadius: 2, display: "inline-block" },
  explain: { fontFamily: "Georgia,serif", fontSize: 13.5, lineHeight: 1.5, color: COL.dim, borderTop: `1px solid ${COL.line}`, paddingTop: 14 },
  footer: { fontSize: 11, color: COL.dim, textAlign: "center", opacity: 0.75 },
};